import { hmacSha256Bytes, sha256Hex } from "./sha256";

/**
 * Provably-fair seed protocol (shared client + server).
 *
 * 1. Server picks a secret `serverSeed`, publishes `commit(serverSeed)` BEFORE
 *    the round (so it can't change the outcome afterwards).
 * 2. Client supplies a `clientSeed`.
 * 3. Both derive the round seeds from HMAC(serverSeed, "clientSeed:nonce").
 * 4. After the round the server reveals `serverSeed`; the client checks the
 *    commit and re-derives + replays to verify the outcome was honest.
 *
 * Note: because skill affects payout here, full verification also needs the
 * player's recorded input ticks (the server logs them) — this module only
 * covers the RNG half (level layout + snap rolls).
 */

export interface DerivedSeeds {
  levelSeed: number;
  fxSeed: number;
}

/** Public commitment to a server seed. */
export function commit(serverSeed: string): string {
  return sha256Hex(serverSeed);
}

/** Verify a revealed server seed against its earlier commitment. */
export function verifyCommit(serverSeed: string, serverSeedHash: string): boolean {
  return commit(serverSeed) === serverSeedHash;
}

function u32(bytes: Uint8Array, offset: number): number {
  return (
    ((bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3]) >>>
    0
  );
}

/**
 * Deterministically derive the round's RNG seeds. Identical in Node and the
 * browser — this is the property the whole fairness guarantee rests on.
 */
export function deriveSeeds(
  serverSeed: string,
  clientSeed: string,
  nonce: number,
): DerivedSeeds {
  const digest = hmacSha256Bytes(serverSeed, `${clientSeed}:${nonce}`);
  return {
    levelSeed: u32(digest, 0),
    fxSeed: u32(digest, 4),
  };
}
